/**
 * TourSpotlight — soft cutout overlay that highlights an element on the
 * real product while a tour scene plays.
 *
 * Behavior
 *   - Looks for the element matching `selector` (e.g. data-testid attribute).
 *   - Uses four positioned `<div>`s to dim everything *except* the target
 *     rect (cheap and works even with transformed/scrolled ancestors).
 *   - Draws a glowing 2px ring + label tag pointing at the element.
 *   - Continuously re-measures (RAF) and **re-scrolls** whenever the target
 *     drifts into the bottom band reserved for the floating narrator panel,
 *     so the spotlight is never hidden behind the panel.
 *   - If the selector doesn't resolve within `maxWaitMs`, the spotlight
 *     silently fails (no dim layer) so the tour never blocks on missing UI.
 *
 * Click-through: the overlay is pointer-events-none so the underlying
 * product remains fully interactive (presenters can still click things).
 */
import React, { useEffect, useState } from 'react';
import { useTour } from '../../context/TourContext';

const PADDING = 10;        // px of breathing room around the highlighted rect
const MAX_WAIT_MS = 4000;  // wait this long for the selector to appear
const POLL_MS = 120;
// The floating narrator panel sits at the bottom of the viewport. Any target
// whose bottom enters this band is considered "occluded" and we scroll it back
// into the safe area above.
const PANEL_RESERVE = 280;
// Desired top of the target as a ratio of the *safe* (non-panel) viewport.
// 0.30 → roughly the upper third — well clear of any tooltips coming off the
// element below, and well above the panel.
const SAFE_TOP_RATIO = 0.30;

function useTargetRect(selector, enabled) {
  const [rect, setRect] = useState(null);

  useEffect(() => {
    if (!enabled || !selector) { setRect(null); return undefined; }
    let rafId = 0;
    let stopped = false;
    let pollTimer = 0;
    let waitedMs = 0;
    let lastScrollTs = 0;
    let scrolledIntoViewFor = null;  // dedupe: only one initial scrollIntoView per selector

    // Walk up the ancestor chain looking for the nearest element that
    // actually scrolls (overflow:auto/scroll + scrollHeight > clientHeight).
    // Many app shells render `<main class="overflow-y-auto">` and the window
    // itself doesn't scroll — `window.scrollBy` is a no-op in that case.
    const findScrollParent = (el) => {
      let node = el?.parentElement;
      while (node && node !== document.body) {
        const cs = getComputedStyle(node);
        if ((cs.overflowY === 'auto' || cs.overflowY === 'scroll') && node.scrollHeight > node.clientHeight + 1) {
          return node;
        }
        node = node.parentElement;
      }
      return null;  // null → fall back to window
    };

    const measure = () => {
      const el = document.querySelector(selector);
      if (!el) { setRect(null); return false; }
      // First time we resolve this selector, ask the browser to scroll the
      // element into view across ALL ancestor scroll containers. After this
      // initial nudge, the panel-reserve loop below takes over to keep the
      // target above the narration panel even if it drifts.
      if (scrolledIntoViewFor !== selector) {
        try { el.scrollIntoView({ block: 'center', inline: 'nearest', behavior: 'smooth' }); } catch (_) { /* ignore */ }
        scrolledIntoViewFor = selector;
      }
      const r = el.getBoundingClientRect();
      if (r.width === 0 && r.height === 0) { setRect(null); return false; }

      // Keep the target out of the panel-reserve band. We measure the real
      // narrator panel's height when present (it varies with narrative
      // length + highlight) and fall back to the conservative default. This
      // prevents tall narration boxes from silently occluding the spotlight.
      const vh = window.innerHeight;
      const panelEl = document.querySelector('[data-testid="tour-panel"]');
      const panelTop = panelEl ? panelEl.getBoundingClientRect().top : (vh - PANEL_RESERVE);
      const panelReserve = Math.max(PANEL_RESERVE, vh - panelTop + 24);  // 24px breathing room
      const safeBottom = vh - panelReserve;
      // Reserve room at the very top of the viewport for the spotlight
      // *label* (which floats above the target). Without this, when the
      // target sits near y=0 the label overflows off-screen.
      const safeTop    = 72;
      const safeHeight = Math.max(120, safeBottom - safeTop);
      const targetHeight = r.height;

      // Decide the DESIRED target-top position (in viewport coords).
      //   - If the target fits inside the safe area (typical for narrow
      //     spotlights on a single field/button/row), aim for SAFE_TOP_RATIO
      //     of the safe area — roughly the upper third, well clear of the
      //     narrator panel below.
      //   - If the target is TALLER than the safe area (an entire card, a
      //     long list, a full-page section), pin its TOP to just below the
      //     label reserve. That guarantees the caption + upper portion of
      //     the target stay visible above the panel, which is where the
      //     "fresh" content the narrator is describing lives. The bottom
      //     may still spill into the panel-reserve band but that's inherent
      //     to the target's size — nothing we can do about it without
      //     shrinking the target itself.
      const fits = targetHeight <= safeHeight - 24;
      const desiredTop = fits
        ? safeTop + safeHeight * SAFE_TOP_RATIO - Math.min(targetHeight / 2, safeHeight * SAFE_TOP_RATIO * 0.6)
        : safeTop + 20;
      // Consider the target "occluded" whenever it's meaningfully out of
      // position. For tall targets the bottom-in-reserve check no longer
      // matters (we accept partial visibility), so we only look at the top.
      const topDelta = r.top - desiredTop;
      const bottomOccluded = fits ? r.bottom > safeBottom : false;
      const topOccluded    = r.top < safeTop;
      const occluded = topOccluded || bottomOccluded || Math.abs(topDelta) > 24;
      const now      = Date.now();
      if (occluded && now - lastScrollTs > 400) {
        try {
          const delta = topDelta;
          if (Math.abs(delta) > 16) {
            // Prefer the nearest scrolling ancestor; fall back to window for
            // pages that scroll the document itself.
            const sp = findScrollParent(el);
            if (sp) {
              sp.scrollBy({ top: delta, left: 0, behavior: 'smooth' });
            } else {
              window.scrollBy({ top: delta, left: 0, behavior: 'smooth' });
            }
            lastScrollTs = now;
          }
        } catch (_) { /* ignore */ }
      }
      setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
      return true;
    };

    const tick = () => {
      if (stopped) return;
      measure();
      rafId = requestAnimationFrame(tick);
    };

    // Poll until the selector resolves (target may render after route navigation)
    const poll = () => {
      if (stopped) return;
      const found = measure();
      if (found) {
        rafId = requestAnimationFrame(tick);
      } else if (waitedMs < MAX_WAIT_MS) {
        waitedMs += POLL_MS;
        pollTimer = setTimeout(poll, POLL_MS);
      } else {
        setRect(null);
      }
    };
    poll();

    return () => {
      stopped = true;
      if (rafId) cancelAnimationFrame(rafId);
      if (pollTimer) clearTimeout(pollTimer);
    };
  }, [selector, enabled]);

  return rect;
}

export default function TourSpotlight() {
  const t = useTour();
  // Prefer the beat-scoped spotlight (set inside TourContext.playBeat) so the
  // frame moves with the narrator sentence-by-sentence. Fall back to the
  // scene-level `spotlight` for legacy tours that don't declare beats.
  const selector = t.activeSpotlight ?? t.currentScene?.spotlight ?? null;
  const label    = t.activeSpotlightLabel ?? t.currentScene?.spotlightLabel ?? null;
  const rect     = useTargetRect(selector, !!t.isActive && !!selector);

  if (!t.isActive || !selector || !rect) return null;

  const r = {
    top:    Math.max(0, rect.top    - PADDING),
    left:   Math.max(0, rect.left   - PADDING),
    width:  rect.width  + PADDING * 2,
    height: rect.height + PADDING * 2,
  };
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  // Label placement: strongly prefer ABOVE the target so it acts as a caption
  // and never intrudes into the target rect. Only flip below when the target
  // is jammed against the top of the viewport (no room above). And guarantee
  // the label never gets pushed into the narrator-panel reserve at the
  // bottom — if that would happen, snap it above the target.
  const panelEl = typeof document !== 'undefined' ? document.querySelector('[data-testid="tour-panel"]') : null;
  const panelTop = panelEl ? panelEl.getBoundingClientRect().top : (vh - 280);
  const roomAbove = r.top - 40;
  const labelAbove = roomAbove >= 40;
  let labelTop = labelAbove ? r.top - 36 : r.top + r.height + 10;
  if (labelTop + 28 > panelTop - 8) {
    // Would collide with panel — force above the target.
    labelTop = Math.max(12, r.top - 36);
  }
  const labelLeft = Math.max(8, Math.min(vw - 280, r.left));

  const dimColor  = 'rgba(15, 23, 42, 0.55)';   // slate-900 / 55%

  return (
    <div data-testid="tour-spotlight" className="fixed inset-0 z-[9998] pointer-events-none" aria-hidden>
      {/* Four dim panels surrounding the target rect */}
      <div style={{ position: 'absolute', top: 0,           left: 0,           width: '100vw',  height: r.top,                background: dimColor, transition: 'all 0.35s ease' }} />
      <div style={{ position: 'absolute', top: r.top,       left: 0,           width: r.left,   height: r.height,             background: dimColor, transition: 'all 0.35s ease' }} />
      <div style={{ position: 'absolute', top: r.top,       left: r.left + r.width, width: vw - (r.left + r.width), height: r.height, background: dimColor, transition: 'all 0.35s ease' }} />
      <div style={{ position: 'absolute', top: r.top + r.height, left: 0,      width: '100vw',  height: vh - (r.top + r.height), background: dimColor, transition: 'all 0.35s ease' }} />

      {/* Glow ring around the cutout (breathing pulse) */}
      <div
        style={{
          position: 'absolute',
          top: r.top,
          left: r.left,
          width: r.width,
          height: r.height,
          borderRadius: 14,
          animation: 'tour-spotlight-pulse 2.4s ease-in-out infinite',
          transition: 'top 0.35s ease, left 0.35s ease, width 0.35s ease, height 0.35s ease',
        }}
      />

      {/* Optional label tag */}
      {label && (
        <div
          style={{ position: 'absolute', top: labelTop, left: labelLeft, transition: 'all 0.35s ease' }}
          className="px-2.5 py-1 rounded-md bg-sky-500 text-white text-[11px] font-semibold shadow-lg max-w-[260px] truncate"
          data-testid="tour-spotlight-label"
        >
          {label}
        </div>
      )}
    </div>
  );
}
